'use client';

export interface ReasoningOption { value: string; label: string }

/** A discrete but draggable reasoning-effort scale: native range semantics with visible labelled stops. */
export function ReasoningScale({ options, value, onChange, ariaLabel, disabled = false }: {
  options: ReasoningOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  const selected = options.findIndex((option) => option.value === value);
  // A long ladder (seven levels) cannot fit its labels side by side in a narrow container, so there every
  // other label rides one row higher and each label gets two columns of room. The @container query
  // measures the scale itself, not the viewport, because the scale lives in menus, modals and cards.
  const stagger = options.length > 4;
  return (
    <div className="@container relative w-full min-w-0 pb-1 pt-1">
      <div className="absolute bottom-2.25 left-3 right-3 h-px bg-border-strong" aria-hidden />
      <div className="relative z-[var(--z-lift)] grid items-end" style={{ gridTemplateColumns: `repeat(${Math.max(options.length, 1)}, minmax(0, 1fr))` }}>
        {options.map((option, index) => {
          const active = index === selected;
          return (
            <button
              key={option.value || 'default'}
              type="button"
              aria-label={option.label}
              aria-pressed={active}
              onClick={() => onChange(option.value)}
              disabled={disabled}
              className={`group flex min-w-0 flex-col items-center gap-2 transition-colors ${active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <span className={`w-full whitespace-nowrap text-center text-small ${stagger && index % 2 === 1 ? '@max-md:mb-4' : ''}`} title={option.label}>{option.label}</span>
              {/* The active stop is a larger dot in the brand fill, and nothing more: a coloured halo around
                  it read as decoration on a settings surface that is otherwise neutral. */}
              <span className={`block rounded-full transition-[width,height,background-color] ${active ? 'h-3.5 w-3.5 bg-primary' : 'h-2.5 w-2.5 bg-subtle-foreground group-hover:bg-muted-foreground'}`} aria-hidden />
            </button>
          );
        })}
      </div>
      <input
        type="range"
        min={0}
        max={Math.max(0, options.length - 1)}
        step={1}
        value={Math.max(0, selected)}
        onChange={(event) => onChange(options[Number(event.target.value)]?.value ?? '')}
        aria-label={ariaLabel}
        disabled={disabled}
        className="absolute inset-x-0 bottom-0 z-[var(--z-sticky)] h-8 w-full cursor-ew-resize opacity-0 disabled:cursor-not-allowed"
      />
    </div>
  );
}
